import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Auth State Machine:
 * - INITIALIZING: Checking for existing session
 * - LOADING_PROFILE: Session found, fetching user profile
 * - READY: Auth check complete (with or without user)
 */

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [orgRole, setOrgRole] = useState(null);
  const [organizations, setOrganizations] = useState([]); // All user's organizations
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  
  // Refs to prevent race conditions
  const initializingRef = useRef(false);
  const profileFetchedForUserRef = useRef(null); // Track which user's profile we fetched

  // Clear all auth state
  const clearAuthState = useCallback(() => {
    setUser(null);
    setSession(null);
    setProfile(null);
    setOrganization(null);
    setOrgRole(null);
    setOrganizations([]);
    profileFetchedForUserRef.current = null;
  }, []);

  // Mark auth as fully initialized
  const finishAuthInit = useCallback(() => {
    console.log('Auth initialization complete');
    setLoading(false);
    setAuthInitialized(true);
    initializingRef.current = false;
  }, []);

  // Fetch user profile and org membership from backend API (bypasses Supabase RLS)
  const fetchUserData = useCallback(async (userId, userEmail, accessToken) => {
    // Prevent duplicate fetches for same user
    if (profileFetchedForUserRef.current === userId && profile) {
      console.log('Profile already fetched for user:', userId);
      return { profile, organization, orgRole, organizations };
    }

    try {
      console.log('Fetching user data for:', userId, userEmail);
      
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      if (!accessToken) {
        console.error('No auth token available');
        const basicProfile = {
          id: userId,
          email: userEmail,
          display_name: userEmail.split('@')[0],
          platform_role: null
        };
        setProfile(basicProfile);
        setOrganization(null);
        setOrgRole(null);
        setOrganizations([]);
        profileFetchedForUserRef.current = userId;
        return { profile: basicProfile, organization: null, orgRole: null, organizations: [] };
      }
      
      // Fetch from backend /api/auth/me endpoint
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error('Backend /api/auth/me error:', response.status);
        // Fallback: create basic profile from Supabase auth
        const basicProfile = {
          id: userId,
          email: userEmail,
          display_name: userEmail.split('@')[0],
          platform_role: null
        };
        setProfile(basicProfile);
        setOrganization(null);
        setOrgRole(null);
        setOrganizations([]);
        profileFetchedForUserRef.current = userId;
        return { profile: basicProfile, organization: null, orgRole: null, organizations: [] };
      }
      
      const userData = await response.json();
      console.log('Backend user data:', userData);
      
      // Set profile
      const currentProfile = userData.profile || {
        id: userId,
        email: userEmail,
        display_name: userEmail.split('@')[0],
        platform_role: null
      };
      setProfile(currentProfile);
      profileFetchedForUserRef.current = userId;
      
      // Store all organizations
      const allOrgs = userData.organizations || [];
      setOrganizations(allOrgs);
      
      // Use active_organization from backend if available (takes priority)
      if (userData.active_organization) {
        const activeOrg = userData.active_organization;
        const org = activeOrg.organization;
        setOrganization(org);
        setOrgRole(activeOrg.role);
        return { 
          profile: currentProfile, 
          organization: org, 
          orgRole: activeOrg.role,
          organizations: allOrgs
        };
      }
      
      // For platform admins without active_organization, allow them to work without an org context
      // But they can still switch to an org if they have memberships
      if (currentProfile?.platform_role && allOrgs.length === 0) {
        setOrganization(null);
        setOrgRole(null);
        return { profile: currentProfile, organization: null, orgRole: null, organizations: allOrgs };
      }
      
      // Fallback: use first organization if no active org set
      if (allOrgs.length > 0) {
        const orgMembership = allOrgs[0];
        const org = {
          id: orgMembership.organization_id,
          name: orgMembership.organization_name,
          slug: orgMembership.organization_slug,
        };
        setOrganization(org);
        setOrgRole(orgMembership.role);
        return { 
          profile: currentProfile, 
          organization: org, 
          orgRole: orgMembership.role,
          organizations: allOrgs
        };
      } else {
        setOrganization(null);
        setOrgRole(null);
        return { profile: currentProfile, organization: null, orgRole: null, organizations: [] };
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      // On error, still set a basic profile to prevent stuck loading
      const basicProfile = {
        id: userId,
        email: userEmail,
        display_name: userEmail.split('@')[0],
        platform_role: null
      };
      setProfile(basicProfile);
      profileFetchedForUserRef.current = userId;
      return { profile: basicProfile, organization: null, orgRole: null, organizations: [] };
    }
  }, [profile, organization, orgRole, organizations]);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // Prevent multiple simultaneous initializations
      if (initializingRef.current) {
        console.log('Auth initialization already in progress, skipping');
        return;
      }
      initializingRef.current = true;
      console.log('Starting auth initialization...');
      
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        console.log('Got session:', currentSession ? 'exists' : 'null');
        
        if (!mounted) return;
        
        if (currentSession?.user) {
          console.log('Session found, loading user data...');
          setSession(currentSession);
          setUser(currentSession.user);
          await fetchUserData(
            currentSession.user.id, 
            currentSession.user.email,
            currentSession.access_token
          );
        }
        
        if (mounted) {
          finishAuthInit();
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        if (mounted) {
          finishAuthInit();
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;
      
      console.log('Auth state change:', event, newSession?.user?.id);
      
      // Skip INITIAL_SESSION if we already initialized (prevents double initialization)
      if (event === 'INITIAL_SESSION' && authInitialized) {
        console.log('Skipping INITIAL_SESSION - already initialized');
        return;
      }
      
      // Handle INITIAL_SESSION - only if we haven't initialized yet
      if (event === 'INITIAL_SESSION' && !authInitialized) {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        if (newSession?.user) {
          await fetchUserData(
            newSession.user.id, 
            newSession.user.email,
            newSession.access_token
          );
        }
        finishAuthInit();
        return;
      }
      
      // Handle sign out
      if (event === 'SIGNED_OUT') {
        clearAuthState();
        finishAuthInit();
        return;
      }
      
      // Handle sign in
      if (event === 'SIGNED_IN' && newSession?.user) {
        setSession(newSession);
        setUser(newSession.user);
        await fetchUserData(
          newSession.user.id, 
          newSession.user.email,
          newSession.access_token
        );
        finishAuthInit();
        return;
      }
      
      // Handle token refresh - only update session, don't refetch profile
      if (event === 'TOKEN_REFRESHED' && newSession) {
        setSession(newSession);
        // Only refetch profile if we don't have one yet
        if (!profile && newSession.user) {
          await fetchUserData(
            newSession.user.id,
            newSession.user.email,
            newSession.access_token
          );
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserData, finishAuthInit, clearAuthState, authInitialized, profile]);

  // Sign in with email and password
  const signIn = async (email, password) => {
    setError(null);
    setLoading(true);
    
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (signInError) {
      setLoading(false);
      setError(signInError.message);
      throw signInError;
    }

    // Update session state immediately
    setSession(data.session);
    setUser(data.user);

    if (data.user && data.session) {
      const userData = await fetchUserData(
        data.user.id, 
        data.user.email,
        data.session.access_token
      );
      setLoading(false);
      return { user: data.user, ...userData };
    }

    setLoading(false);
    return { user: data.user };
  };

  // Sign up with email, password, and display name
  const signUp = async (email, password, displayName) => {
    setError(null);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName,
        },
        emailRedirectTo: `${window.location.origin}/onboarding`,
      },
    });

    if (signUpError) {
      setError(signUpError.message);
      throw signUpError;
    }

    return data;
  };

  // Sign out
  const signOut = async () => {
    const { error: signOutError } = await supabase.auth.signOut();
    if (signOutError) {
      setError(signOutError.message);
      throw signOutError;
    }
    
    clearAuthState();
  };

  // Switch to a different organization
  const switchOrganization = async (organizationId) => {
    if (!user) throw new Error('No authenticated user');
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const currentSession = await supabase.auth.getSession();
    const token = currentSession?.data?.session?.access_token;
    
    if (!token) {
      throw new Error('No auth token available');
    }
    
    const response = await fetch(`${API_URL}/api/auth/switch-organization/${organizationId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Failed to switch organization');
    }
    
    const data = await response.json();
    
    if (data.active_organization) {
      setOrganization(data.active_organization.organization);
      setOrgRole(data.active_organization.role);
    }
    
    return data;
  };

  // Create organization (for org onboarding)
  const createOrganization = async (orgData, campusName) => {
    if (!user) throw new Error('No authenticated user');

    // First, get or create campus
    let campusId = null;
    if (campusName) {
      const campusSlug = campusName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      
      // Try to find existing campus
      const { data: existingCampus } = await supabase
        .from('campuses')
        .select('id')
        .eq('slug', campusSlug)
        .single();

      if (existingCampus) {
        campusId = existingCampus.id;
      } else {
        // Create new campus
        const { data: newCampus, error: campusError } = await supabase
          .from('campuses')
          .insert({
            name: campusName,
            slug: campusSlug,
          })
          .select()
          .single();

        if (campusError) throw campusError;
        campusId = newCampus.id;
      }
    }

    // Create organization
    const orgSlug = orgData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    
    const { data: newOrg, error: orgError } = await supabase
      .from('organizations')
      .insert({
        name: orgData.name,
        slug: orgSlug,
        type: orgData.type,
        campus_id: campusId,
        membership_size: orgData.membershipSize || null,
        description: orgData.description || null,
      })
      .select()
      .single();

    if (orgError) throw orgError;

    // Create organization_users record with owner role
    const { error: memberError } = await supabase
      .from('organization_users')
      .insert({
        organization_id: newOrg.id,
        user_id: user.id,
        role: 'owner',
        status: 'active',
      });

    if (memberError) throw memberError;

    // Update profile with campus
    if (campusId) {
      await supabase
        .from('profiles')
        .update({ campus_id: campusId })
        .eq('id', user.id);
    }

    setOrganization(newOrg);
    setOrgRole('owner');

    return newOrg;
  };

  // Check permissions
  const hasPermission = (permission) => {
    if (!orgRole) return false;
    
    const permissions = {
      owner: ['manage_org', 'manage_events', 'manage_guests', 'manage_members', 'view_analytics', 'scan_passes'],
      admin: ['manage_events', 'manage_guests', 'manage_members', 'view_analytics', 'scan_passes'],
      event_manager: ['manage_events', 'manage_guests', 'view_analytics', 'scan_passes'],
      door_staff: ['scan_passes'],
      member: [],
    };
    
    return permissions[orgRole]?.includes(permission) ?? false;
  };

  // Check if user is platform admin
  const isPlatformAdmin = useCallback(() => {
    return profile?.platform_role === 'platform_owner' || profile?.platform_role === 'platform_admin';
  }, [profile]);

  // Check if user needs onboarding (has profile but no org and is not platform admin)
  // Students without orgs are fine - they just use student features
  const needsOnboarding = useCallback(() => {
    if (!profile) return false;
    // Platform admins don't need onboarding
    if (profile.platform_role) return false;
    // Users with organizations don't need onboarding
    if (organization || orgRole) return false;
    // Users with any org memberships don't need onboarding
    if (organizations && organizations.length > 0) return false;
    // Students without orgs are allowed - they can still use discover, passport, billing
    // Only require onboarding if explicitly flagged or for specific org flows
    return false; // Students can exist without orgs
  }, [profile, organization, orgRole, organizations]);

  const value = {
    user,
    session,
    profile,
    organization,
    orgRole,
    organizations, // All user's organizations for switcher
    loading,
    error,
    authInitialized, // New: indicates if initial auth check is complete
    signIn,
    signUp,
    signOut,
    switchOrganization, // New: switch between organizations
    createOrganization,
    hasPermission,
    fetchUserData,
    isPlatformAdmin,
    needsOnboarding,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
